

import { LeadContact } from "@/services/leadContactsService";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Linkedin, Crown, MoreHorizontal, Edit, Eye, Trash2, MessageSquare } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

interface ContactCardProps {
  contact: LeadContact;
  onEdit?: (contact: LeadContact) => void;
  onView?: (contact: LeadContact) => void;
  onDelete?: (contact: LeadContact) => void;
  onInitiateCall?: (contact: LeadContact) => void;
}

const ContactCard = ({ contact, onEdit, onView, onDelete, onInitiateCall }: ContactCardProps) => {
  const getInitials = (firstName: string, lastName: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    if (lastName) {
      return lastName.substring(0, 2).toUpperCase();
    }
    return "?";
  };

  const getDepartmentColor = (department: string | undefined) => {
    switch (department?.toLowerCase()) {
      case 'product':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'design':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'engineering':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'marketing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'sales':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'finance':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'hr':
      case 'human resources':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const handleCall = () => {
    console.log('📞 ContactCard: handleCall called for contact:', contact.first_name, contact.last_name);
    console.log('📞 ContactCard: phone_number:', contact.phone_number, 'phone:', contact.phone);
    
    const phoneNumber = contact.phone_number || contact.phone;
    if (phoneNumber && onInitiateCall) {
      // Use Twilio calling with confirmation dialog
      onInitiateCall(contact);
    } else if (phoneNumber) {
      // Fallback to tel: link if no onInitiateCall provided
      window.open(`tel:${phoneNumber}`, '_self');
    }
  };

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCall();
  };

  const handleEmail = () => {
    if (contact.email) {
      window.open(`mailto:${contact.email}`, '_self');
    }
  };

  const handleSMS = () => {
    const phoneNumber = contact.phone_number || contact.phone;
    if (phoneNumber) {
      window.open(`sms:${phoneNumber}`, '_self');
    }
  };

  return (
    <Card className="overflow-hidden h-full">
      <CardContent className="p-0 flex flex-col h-full">
        <div className="p-6 flex-1">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 border">
                <AvatarImage src={contact.avatar_url || undefined} />
                <AvatarFallback className="text-lg">
                  {getInitials(contact.first_name, contact.last_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg leading-none">
                  {contact.first_name} {contact.last_name}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {contact.designation || "No designation"}
                </p>
                <div className="flex items-center mt-2 gap-1.5">
                  {contact.department && (
                    <Badge variant="outline" className={`${getDepartmentColor(contact.department)} border-0`}>
                      {contact.department}
                    </Badge>
                  )}
                  {contact.is_primary && (
                    <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0">
                      <Crown className="h-3 w-3 mr-1" /> Primary
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[160px]">
                {onView && (
                  <DropdownMenuItem onClick={() => onView(contact)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(contact)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Contact
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {(contact.phone_number || contact.phone) && (
                  <DropdownMenuItem onClick={handleCall}>
                    <Phone className="mr-2 h-4 w-4" />
                    Call
                  </DropdownMenuItem>
                )}
                {contact.email && (
                  <DropdownMenuItem onClick={handleEmail}>
                    <Mail className="mr-2 h-4 w-4" />
                    Email
                  </DropdownMenuItem>
                )}
                {(contact.phone_number || contact.phone) && (
                  <DropdownMenuItem onClick={handleSMS}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    SMS
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem>Add to Sequence</DropdownMenuItem>
                <DropdownMenuSeparator />
                {onDelete && (
                  <DropdownMenuItem 
                    className="text-destructive" 
                    onClick={() => onDelete(contact)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="grid grid-cols-1 gap-2 mt-4">
            {(() => {
              console.log('📧 ContactCard: Rendering contact info for:', contact.first_name, contact.last_name);
              console.log('📧 ContactCard: email:', contact.email, typeof contact.email);
              console.log('📞 ContactCard: phone:', contact.phone, typeof contact.phone, 'phone_number:', contact.phone_number);
              return null;
            })()}
            {/* Email handling for both string and JSON array */}
            {contact.email && (
              <div className="space-y-1">
                {typeof contact.email === 'string' && contact.email.trim() ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${contact.email}`} className="text-primary hover:underline truncate">
                      {contact.email}
                    </a>
                  </div>
                ) : Array.isArray(contact.email) && contact.email.length > 0 ? (
                  contact.email.map((emailObj, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-2">
                        <a href={`mailto:${emailObj.email}`} className="text-primary hover:underline truncate">
                          {emailObj.email}
                        </a>
                        <span className="text-xs text-muted-foreground capitalize">• {emailObj.type}</span>
                        {emailObj.is_primary && (
                          <span className="text-xs font-medium text-blue-600">Primary</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            )}
            
            {/* Phone handling for both string and JSON array */}
            {((contact.phone_number && contact.phone_number.trim()) || 
              (typeof contact.phone === 'string' && contact.phone.trim()) || 
              (Array.isArray(contact.phone) && contact.phone.length > 0)) && (
              <div className="space-y-1">
                {contact.phone_number && contact.phone_number.trim() ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <button 
                      onClick={handlePhoneClick}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      {contact.phone_number}
                    </button>
                  </div>
                ) : typeof contact.phone === 'string' && contact.phone.trim() ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <button 
                      onClick={handlePhoneClick}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      {contact.phone}
                    </button>
                  </div>
                ) : Array.isArray(contact.phone) && contact.phone.length > 0 ? (
                  contact.phone.map((phoneObj, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={handlePhoneClick}
                          className="text-primary hover:underline cursor-pointer"
                        >
                          {phoneObj.phone}
                        </button>
                        <span className="text-xs text-muted-foreground capitalize">• {phoneObj.type}</span>
                        {phoneObj.is_primary && (
                          <span className="text-xs font-medium text-blue-600">Primary</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            )}
            
            {contact.linkedin_url && (
              <div className="flex items-center gap-2 text-sm">
                <Linkedin className="h-4 w-4 text-muted-foreground" />
                <a 
                  href={contact.linkedin_url.startsWith('http') ? contact.linkedin_url : `https://${contact.linkedin_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline truncate"
                >
                  LinkedIn Profile
                </a>
              </div>
            )}
          </div>
        </div>
        
        <div className="border-t border-border p-4 bg-muted/20 flex justify-between items-center mt-auto">
          <div className="text-sm">
            {contact.influence_score && (
              <span className="font-medium">Influence: {contact.influence_score}/10</span>
            )}
          </div>
          <div className="flex gap-2">
            {(contact.phone_number || contact.phone) && (
              <Button size="sm" variant="outline" onClick={handleCall}>
                <Phone className="h-3.5 w-3.5" />
              </Button>
            )}
            {contact.email && (
              <Button size="sm" variant="outline" onClick={handleEmail}>
                <Mail className="h-3.5 w-3.5" />
              </Button>
            )}
            {onView && (
              <Button size="sm" variant="outline" onClick={() => onView(contact)}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(contact)}>
                <Edit className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactCard;
